import { motion } from "motion/react";
import { DISHA_CONTACT, WARNING_MESSAGES } from "../../lib/constants";

interface WarningMessageProps {
  message: string;
  warningNumber: number;
  isBlocked: boolean;
  timestamp: Date;
  showContact?: boolean;
  hasShownContact?: boolean;
}

export function WarningMessage({
  message,
  warningNumber,
  isBlocked,
  timestamp,
  showContact,
  hasShownContact,
}: WarningMessageProps) {
  // Override cheerful backend message in emergency contexts
  const displayMessage =
    isBlocked && hasShownContact ? WARNING_MESSAGES.blockedEmergency : message;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="flex justify-start mb-4"
    >
      <div
        className={`
          relative max-w-[85%] md:max-w-[70%] px-5 py-4 rounded-2xl rounded-bl-md
          ${
            isBlocked
              ? "bg-rose-50 border-2 border-rose-300 text-rose-800"
              : "bg-amber-50 border-2 border-amber-300 text-amber-800"
          }
        `}
      >
        <div className="flex gap-2 items-center mb-2">
          <svg
            className={`h-5 w-5 ${isBlocked ? "text-rose-500" : "text-amber-500"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <title>{isBlocked ? "Blocked" : "Warning"}</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="font-semibold text-xs tracking-wide uppercase">
            {isBlocked ? "Conversation Ended" : `Notice`}
          </span>
          {!isBlocked && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                warningNumber === 1
                  ? "bg-amber-200 text-amber-700"
                  : "bg-amber-300 text-amber-800"
              }`}
            >
              {warningNumber === 1 ? "1st reminder" : "Final reminder"}
            </span>
          )}
        </div>

        {/* Render the base message from the backend, and append the NOTE if buttons are showing */}
        <p className="leading-relaxed text-[15px]">
          {displayMessage}
          {isBlocked && showContact && (
            <>
              <br />
              <br />
              {WARNING_MESSAGES.supportNote}
            </>
          )}
        </p>

        {/* Conditionally render contact info inside blocked card */}
        {isBlocked && showContact && (
          <div className="mt-4 pt-4 border-t border-rose-200/60">
            <div className="flex flex-col gap-2 sm:flex-row">
              <a
                href={`mailto:${DISHA_CONTACT.email}`}
                className="bg-white/70 border border-rose-200/50 duration-200 flex font-medium gap-2 hover:bg-white hover:border-rose-300 hover:shadow-md items-center px-4 py-2 rounded-xl shadow-sm text-rose-800 text-sm transition-all"
              >
                <span className="text-lg">📧</span>
                {DISHA_CONTACT.email}
              </a>

              <a
                href={`tel:${DISHA_CONTACT.phone.replace(/[^+\d]/g, "")}`}
                className="bg-white/70 border border-rose-200/50 duration-200 flex font-medium gap-2 hover:bg-white hover:border-rose-300 hover:shadow-md items-center px-4 py-2 rounded-xl shadow-sm text-rose-800 text-sm transition-all"
              >
                <span className="text-lg">📞</span>
                {DISHA_CONTACT.phone}
              </a>
            </div>
          </div>
        )}

        <div className="mt-2 text-[11px] opacity-50">
          {timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </motion.div>
  );
}
